/**
 * MUSM Premium Storefront — Animation System
 * Lenis smooth scroll + GSAP ScrollTrigger + SplitType
 */

document.addEventListener('DOMContentLoaded', () => {
  // Guard — require GSAP
  if (typeof gsap === 'undefined') {
    console.warn('MUSM: GSAP not loaded.');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // ─── Lenis smooth scroll ────────────────────────────────────────
  let lenis = null;
  if (typeof Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.8,
    });

    // Sync Lenis RAF with GSAP ticker
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    // Tell ScrollTrigger about Lenis scroll
    lenis.on('scroll', ScrollTrigger.update);
  }



  // ─── Run all animations ─────────────────────────────────────────
  initNav();
  initHero();
  initLookbook();
  initManifesto();
  initFooterCta();
  initHamburger();
  initAboutPage();
  initGiftingCarousel();
});





/* ================================================================
   NAV REVEAL
   Fades in from top after a short delay
   ================================================================ */
function initNav() {
  const nav = document.getElementById('musmNav');
  if (!nav) return;

  gsap.to(nav, {
    opacity: 1,
    y: 0,
    duration: 1.2,
    ease: 'power3.out',
    delay: 2.4, // After hero sequence plays
  });
}


/* ================================================================
   HERO SEQUENCE
   Staggered cinematic entry animation
   ================================================================ */
function initHero() {
  const logoWrap = document.getElementById('heroLogoWrap');
  const title   = document.getElementById('heroTitle');
  const rule    = document.getElementById('heroRule');
  const body    = document.getElementById('heroBody');
  const cta     = document.getElementById('heroCta');
  const visual  = document.getElementById('heroVisual');
  const scroll  = document.getElementById('heroScroll');

  if (!title) return;

  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

  // 1. Logo fades in and scales from 0.9 to 1
  if (logoWrap) {
    tl.to(logoWrap, { opacity: 1, scale: 1, duration: 1.2, ease: 'power4.out' }, 0.2);
  }

  // 3. Title — SplitType char reveal
  if (typeof SplitType !== 'undefined') {
    const split = new SplitType(title, { types: 'chars, words' });
    gsap.set(title, { opacity: 1 });
    tl.from(split.chars, {
      opacity: 0,
      y: 40,
      rotateX: -20,
      stagger: 0.02,
      duration: 0.8,
      ease: 'back.out(1.2)',
    }, 0.8);
  } else {
    tl.to(title, { opacity: 1, y: 0, duration: 1.2 }, 0.8);
  }

  // 4. Gold rule draws
  tl.to(rule, {
    width: '280px',
    duration: 1.0,
    ease: 'power2.inOut',
  }, 1.2);

  // 5. Body text fades
  tl.to(body, { opacity: 1, y: 0, duration: 1.0 }, 1.5);

  // 6. CTA fades in
  tl.to(cta, { opacity: 1, y: 0, duration: 0.8 }, 1.8);

  // 7. Right Visual Panel (Floating Bottles) transitions in
  tl.to(visual, {
    opacity: 1,
    y: 0,
    duration: 1.4,
    ease: 'power4.out',
  }, 1.2);

  // 8. Scroll cue pulses in
  tl.to(scroll, { opacity: 1, duration: 0.8 }, 2.2);

  // 9. Optimize Hero Video Playback: pause when scrolled out of view
  ScrollTrigger.create({
    trigger: '#musmHero',
    start: 'top top',
    end: 'bottom top',
    onLeave: () => {
      const heroVideo = document.querySelector('.hero-ambient-video');
      if (heroVideo) heroVideo.pause();
    },
    onEnterBack: () => {
      const heroVideo = document.querySelector('.hero-ambient-video');
      if (heroVideo && heroVideo.paused) {
        heroVideo.play().catch(() => {});
      }
    }
  });
}


/* ================================================================
   LOOKBOOK PIN — 3-beat vertical scrub
   ================================================================ */
function initLookbook() {
  const lookbook  = document.getElementById('musmLookbook');
  const slides    = gsap.utils.toArray('.musm-lookbook__slide');
  const progress  = document.getElementById('lookbookProgress');

  const noirVid   = document.querySelector('.musm-portal__video.noir-vid');
  const veloraVid = document.querySelector('.musm-portal__video.velora-vid');
  const bottleNoir   = document.querySelector('.musm-portal__bottle.js-bottle-noir');
  const bottleVelora = document.querySelector('.musm-portal__bottle.js-bottle-velora');

  if (!lookbook || slides.length === 0) return;

  let mm = gsap.matchMedia();

  mm.add('(min-width: 990px)', () => {
    // Set initial desktop states
    gsap.set(slides, { opacity: 0, y: 50, pointerEvents: 'none' });
    gsap.set(slides[0], { opacity: 1, y: 0, pointerEvents: 'auto' });

    // Establish initial reversible styling values using GSAP directly
    if (noirVid) {
      noirVid.play().catch(() => {});
      gsap.set(noirVid, { opacity: 0.9 });
    }
    if (veloraVid) {
      veloraVid.pause();
      gsap.set(veloraVid, { opacity: 0 });
    }
    gsap.set(bottleNoir, { opacity: 1, scale: 1, x: 28, y: 30 });
    gsap.set(bottleVelora, { opacity: 0, scale: 0.88, x: 28, y: 30 });

    // Build the pinned timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: lookbook,
        pin: true,
        scrub: 1.2,
        start: 'top top',
        end: '+=2800',
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (progress) progress.style.height = `${self.progress * 100}%`;



          // PERFORMANCE LAG FIX: Play/pause with wider margins to avoid mid-crossfade pausing
          if (self.progress <= 0.5) {
            if (noirVid && noirVid.paused) noirVid.play().catch(() => {});
          } else {
            if (noirVid && !noirVid.paused) noirVid.pause();
          }

          if (self.progress > 0.1 && self.progress <= 0.85) {
            if (veloraVid && veloraVid.paused) veloraVid.play().catch(() => {});
          } else {
            if (veloraVid && !veloraVid.paused) veloraVid.pause();
          }
        },
      },
    });

    // ── Beat 1 → 2: Noir to Velora (Transition centered around scroll timeline midpoints) ──
    tl.to(slides[0], { opacity: 0, y: -60, pointerEvents: 'none', duration: 1 }, 0)
      
      // Crossfade background videos inside the frame
      .to(noirVid, { opacity: 0, duration: 0.8 }, 0.2)
      .to(veloraVid, { opacity: 0.9, duration: 0.8 }, 0.2)
      
      // Crossfade floating bottles
      .to(bottleNoir, { opacity: 0, scale: 0.88, duration: 0.8 }, 0.2)
      .to(bottleVelora, { opacity: 1, scale: 1, duration: 0.8 }, 0.2)
      
      // Fade in Velora slide text
      .to(slides[1], { opacity: 1, y: 0, pointerEvents: 'auto', duration: 1 }, 1.1)

    // ── Beat 2 → 3: Velora to Discovery / Accessories ─────────────────────
      .to(slides[1], { opacity: 0, y: -60, pointerEvents: 'none', duration: 1 }, 2.2)
      
      // Fade out Velora bottle to leave a clean portal view
      .to(bottleVelora, { opacity: 0, scale: 0.88, duration: 0.8 }, 2.4)
      
      // Fade in Discovery list slide text
      .to(slides[2], { opacity: 1, y: 0, pointerEvents: 'auto', duration: 1 }, 3.3)

    // ── Beat 3 → Exit: Smoothly fade everything to black before unpinning ──
      .to(slides[2], { opacity: 0, y: -60, pointerEvents: 'none', duration: 1 }, 4.4)
      .to('.musm-lookbook__visual-col', { opacity: 0, duration: 1 }, 4.4)
      .to('.musm-lookbook__progress-track', { opacity: 0, duration: 1 }, 4.4);

    return () => {
      tl.kill();
      slides.forEach(s => gsap.set(s, { clearProps: 'all' }));
      gsap.set([noirVid, veloraVid, bottleNoir, bottleVelora], { clearProps: 'all' });
    };
  });

  // Mobile — static stacked layout
  mm.add('(max-width: 989px)', () => {
    slides.forEach(s => gsap.set(s, { clearProps: 'all' }));
    gsap.set([noirVid, veloraVid, bottleNoir, bottleVelora], { clearProps: 'all' });
    
    // Make sure all products are visible in mobile layout
    if (noirVid) noirVid.classList.add('active');
    if (veloraVid) veloraVid.classList.add('active');
    if (bottleNoir) bottleNoir.classList.add('active');
    if (bottleVelora) bottleVelora.classList.add('active');
  });
}


/* ================================================================
   MANIFESTO — word-by-word quote reveal
   ================================================================ */
function initManifesto() {
  const quote = document.getElementById('manifestoQuote');
  const craftItems = gsap.utils.toArray('.musm-craft-item');

  if (quote) {
    if (typeof SplitType !== 'undefined') {
      const split = new SplitType(quote, { types: 'words' });
      gsap.fromTo(split.words, 
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.06,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: quote,
            start: 'top 75%',
            toggleActions: 'play none none none',
          },
        }
      );
    } else {
      gsap.from(quote, {
        opacity: 0,
        y: 40,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: quote,
          start: 'top 80%',
        },
      });
    }
  }

  // Craft columns stagger in
  if (craftItems.length) {
    gsap.from(craftItems, {
      opacity: 0,
      y: 40,
      stagger: 0.15,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: craftItems[0].closest('.musm-craft-grid'),
        start: 'top 80%',
      },
    });
  }
}


/* ================================================================
   FOOTER CTA — slide up entry
   ================================================================ */
function initFooterCta() {
  const eyebrow = document.querySelector('.musm-footer-cta__eyebrow');
  const title   = document.querySelector('.musm-footer-cta__title');
  const buttons = document.querySelector('.musm-footer-cta__buttons');

  const targets = [eyebrow, title, buttons].filter(Boolean);
  if (!targets.length) return;

  gsap.from(targets, {
    opacity: 0,
    y: 50,
    stagger: 0.15,
    duration: 1.0,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: document.getElementById('musmFooterCta'),
      start: 'top 80%',
    },
  });
}


/* ================================================================
   HAMBURGER MENU (mobile)
   ================================================================ */
function initHamburger() {
  const btn  = document.getElementById('musmHamburger');
  const menu = document.getElementById('musmMobileMenu');
  if (!btn || !menu) return;

  btn.addEventListener('click', () => {
    const open = menu.classList.toggle('open');
    btn.setAttribute('aria-expanded', open);
    menu.setAttribute('aria-hidden', !open);
  });
}


/* ================================================================
   ABOUT PAGE CUSTOM ANIMATIONS
   Staggered entry and scroll-based reveals for custom chapters
   ================================================================ */
function initAboutPage() {
  const stage = document.getElementById('musm-about-stage');
  if (!stage) return;

  const logoWrap    = document.getElementById('aboutHeroLogo');
  const subHeadline = document.getElementById('aboutHeroSub');
  const headline    = document.getElementById('aboutHeroHead');
  const rule        = document.getElementById('aboutHeroRule');
  const narrative   = document.getElementById('aboutHeroBody');

  const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

  // 1. Hero Logo animation
  if (logoWrap) {
    heroTl.to(logoWrap, { opacity: 1, scale: 1, duration: 1.2, ease: 'power4.out' }, 0.2);
  }

  // 2. Sub-headline
  if (subHeadline) {
    heroTl.to(subHeadline, { opacity: 1, y: 0, duration: 1.0 }, 0.5);
  }

  // 3. Headline SplitType reveal
  if (headline) {
    if (typeof SplitType !== 'undefined') {
      const split = new SplitType(headline, { types: 'chars, words' });
      gsap.set(headline, { opacity: 1 });
      heroTl.from(split.chars, {
        opacity: 0,
        y: 40,
        rotateX: -20,
        stagger: 0.02,
        duration: 0.8,
        ease: 'back.out(1.2)',
      }, 0.8);
    } else {
      heroTl.to(headline, { opacity: 1, y: 0, duration: 1.2 }, 0.8);
    }
  }

  // 4. Gold rule draws
  if (rule) {
    heroTl.to(rule, {
      width: '280px',
      duration: 1.0,
      ease: 'power2.inOut',
    }, 1.2);
  }

  // 5. Narrative body fades in
  if (narrative) {
    heroTl.to(narrative, { opacity: 1, y: 0, duration: 1.0 }, 1.5);
  }

  // 6. Section 2 (Purpose) ScrollTrigger reveal
  const purposeText = document.querySelector('.musm-about-purpose__text-inner');
  if (purposeText) {
    gsap.to(purposeText, {
      opacity: 1,
      y: 0,
      duration: 1.0,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '#aboutPurpose',
        start: 'top 75%',
      }
    });
  }

  // 7. Section 3 (Craftsmanship) ScrollTrigger reveals
  const craftText   = document.querySelector('.musm-about-craft__content');
  const craftVisual = document.querySelector('.musm-about-craft__visual');
  if (craftText && craftVisual) {
    gsap.to(craftText, {
      opacity: 1,
      y: 0,
      duration: 1.0,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '#aboutCraft',
        start: 'top 75%',
      }
    });
    gsap.to(craftVisual, {
      opacity: 1,
      y: 0,
      duration: 1.2,
      ease: 'power4.out',
      scrollTrigger: {
        trigger: '#aboutCraft',
        start: 'top 75%',
      }
    });
  }

  // 8. Section 4 (Pillars) Cards Stagger reveal
  const pillarsHeader = document.querySelector('.musm-about-pillars__header');
  const pillarCards   = gsap.utils.toArray('.musm-pillar-card');
  if (pillarsHeader && pillarCards.length) {
    gsap.to(pillarsHeader, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      scrollTrigger: {
        trigger: '#aboutPillars',
        start: 'top 80%',
      }
    });

    gsap.to(pillarCards, {
      opacity: 1,
      y: 0,
      stagger: 0.15,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.musm-pillars-grid',
        start: 'top 80%',
      }
    });
  }
}


/* ================================================================
   THE ART OF THE GIFT — Packaging Slide Crossfader
   ================================================================ */
function initGiftingCarousel() {
  const dots = document.querySelectorAll('.musm-gifting__carousel-dot');
  const slides = document.querySelectorAll('.musm-gifting__carousel-slide');
  if (dots.length === 0 || slides.length === 0) return;

  let currentSlide = 0;

  dots.forEach(dot => {
    dot.addEventListener('click', () => {
      const index = parseInt(dot.getAttribute('data-go-to'));
      currentSlide = index;
      
      // Update active dot
      dots.forEach(d => d.classList.remove('active'));
      dot.classList.add('active');

      // Crossfade slides
      slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('active');
          gsap.fromTo(slide, 
            { opacity: 0, y: 15, visibility: 'visible' }, 
            { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }
          );
        } else {
          slide.classList.remove('active');
          gsap.to(slide, { 
            opacity: 0, 
            duration: 0.4,
            onComplete: () => {
              if (!slide.classList.contains('active')) {
                gsap.set(slide, { visibility: 'hidden' });
              }
            }
          });
        }
      });
    });
  });

  // Auto transition every 6 seconds
  let interval = setInterval(() => {
    currentSlide = (currentSlide + 1) % slides.length;
    if (dots[currentSlide]) dots[currentSlide].click();
  }, 6000);

  // Pause on hover
  const carousel = document.getElementById('giftingCarousel');
  if (carousel) {
    carousel.addEventListener('mouseenter', () => clearInterval(interval));
    carousel.addEventListener('mouseleave', () => {
      interval = setInterval(() => {
        currentSlide = (currentSlide + 1) % slides.length;
        if (dots[currentSlide]) dots[currentSlide].click();
      }, 6000);
    });
  }
}
